// 接单记录页面
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '@/i18n';
import { getOrderRecord, updateOrderStatus } from '@/api/airportTransfer';
import { Toast, ListView, PullToRefresh } from "antd-mobile";
import OrderCard from '../components/OrderCard';
import './OrderRecord.scss';

const OrderRecord = () => {
  const history = useHistory();
  const t = useTranslation();
  const [orderRecordData, setOrderRecordData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 10,
    total: 0
  });

  // 创建ListView的数据源
  const dataSource = new ListView.DataSource({
    rowHasChanged: (r1, r2) => r1 !== r2,
  });

  // 获取接单记录数据
  const fetchOrderRecordData = async (pageNo = 1, isLoadMore = false) => {
    setLoading(true);
    try {
      const response = await getOrderRecord({
        countFlag: true,
        pageNo,
        pageSize: pagination.pageSize
      });
      
      if (response && response.data && response.data.code === 200) {
        const newData = response.data.data.datas || [];
        newData.forEach(e => {
          e.price = e.price ? (e.price / 100).toFixed(2) : '0.00';
        });
        
        if (isLoadMore) {
          setOrderRecordData(prevData => [...prevData, ...newData]);
        } else {
          setOrderRecordData(newData);
        }
        
        setPagination({
          ...pagination,
          pageNo: response.data.data.currentPage || pageNo,
          total: response.data.data.total || 0
        });
      } else if (response.data && response.data.errorMsg) {
        console.error(response.data.errorMsg);
        Toast.offline(response.data.errorMsg, 2);
      }
    } catch (error) {
      console.error('获取接单记录数据失败', error);
      Toast.offline(t('airportTransfer.error.fetchFailed'), 2);
    } finally {
      setLoading(false);
    }
  };

  // 更新订单状态处理
  const handleUpdateOrderStatus = async (orderId, status) => {
    const statusTextMap = {
      3: t('orderCard.action.arrivedPickup'),
      4: t('orderCard.action.passengerBoarded'),
      5: t('orderCard.action.orderEnd')
    };
    
    const actionText = statusTextMap[status] || t('airportTransfer.record.update');
    
    Toast.loading(t('airportTransfer.record.processing'), 0, () => {}, true);
    try {
      const response = await updateOrderStatus(orderId, status.toString());
      Toast.hide();
      if (response && response.data && response.data.code === 200) {
        Toast.success(`${actionText}${t('common.success')}`);
        fetchOrderRecordData(1, false);
      } else {
        console.error('更新订单状态失败', response);
        Toast.fail(t('airportTransfer.record.updateFailed') + ': ' + (response?.data?.errorMsg || t('airportTransfer.error.unknown')), 2);
      }
    } catch (error) {
      console.error('更新订单状态出错', error);
      Toast.hide();
      Toast.fail(t('airportTransfer.record.updateError'), 2);
    }
  };

  // 跳转到聊天页面
  const toChat = (orderId) => {
    history.push(`/messageCenter/chat/${orderId}`);
  };

  // 处理加载更多
  const handleLoadMore = (pageNo) => {
    fetchOrderRecordData(pageNo, true);
  };

  // 渲染每一行数据
  const renderRow = (order) => (
    <OrderCard
      key={order.orderId}
      order={order}
      type="record"
      onAction={handleUpdateOrderStatus}
      onChat={toChat}
    />
  );

  // 渲染底部加载提示
  const renderFooter = () => (
    <>
      {loading ? (
        <div className="loading-more">{t('airportTransfer.list.loadingMore')}</div>
      ) : (
        pagination.pageNo * pagination.pageSize >= pagination.total ? (
          <div className="loading-no-more">{t('airportTransfer.list.noMore')}</div>
        ) : null
      )}
    </>
  );

  const onEndReached = () => {
    if (!loading && pagination.pageNo * pagination.pageSize < pagination.total) {
      handleLoadMore(pagination.pageNo + 1);
    }
  };

  // 初始加载数据
  useEffect(() => {
    fetchOrderRecordData(1, false);
  }, []);

  return (
    <div className="order-record-page">
      {/* <div className="page-header">
        <button className="back-btn" onClick={() => history.goBack()}>
          ← 返回
        </button>
        <h1 className="page-title">接单记录</h1>
        <div className="header-placeholder"></div>
      </div> */}

      <div className="order-list-container">
        {orderRecordData.length === 0 && !loading ? (
          <div className="empty-container">
            <div className="empty-icon">📋</div>
            <div className="empty-text">{t('airportTransfer.record.emptyText')}</div>
            <div className="empty-tips">{t('airportTransfer.record.emptyTips')}</div>
          </div>
        ) : (
          <ListView
            dataSource={dataSource.cloneWithRows(orderRecordData)}
            renderRow={renderRow}
            renderFooter={renderFooter}
            style={{ height: 'calc(100vh - 1.2rem)', overflow: 'auto' }}
            pageSize={pagination.pageSize}
            onEndReached={onEndReached}
            onEndReachedThreshold={10}
            scrollRenderAheadDistance={500}
            initialListSize={pagination.pageSize}
            pullToRefresh={
              <PullToRefresh
                refreshing={loading}
                onRefresh={() => fetchOrderRecordData(1, false)}
                distanceToRefresh={25}
                indicator={{
                  activate: t('airportTransfer.pull.activate'),
                  deactivate: t('airportTransfer.pull.deactivate'),
                  release: t('airportTransfer.pull.release'),
                  finish: t('airportTransfer.pull.finish'),
                }}
              />
            }
          />
        )}
      </div>
    </div>
  );
};

export default OrderRecord;
